import { createContext, useContext, ReactNode } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { AuthenticationService } from 'src/api/authentication_service';
import { OrganizationFieldsFragment } from 'src/types';
import { RootLayoutLoaderData } from 'src/types/loaders';
import { UserRole } from 'src/types/user_roles';
import {
  hasRole,
  isClinicAdmin,
  isLabTech,
  canManageMembers,
  isCustomerSuccess,
  isOrderingPhysician,
  isSalesUser,
  isCustomerSuccessOrSalesUser,
} from 'src/utils';

/** Route id for the authenticated root layout (see router). */
export const ROOT_LAYOUT_ROUTE_ID = 'root';

// Types for our auth store
export interface UserPermissions {
  canCreateOrders: boolean;
  // TODO: Add other permissions as needed
}

export interface UserInfo {
  id: string;
  email: string;
  name: string;
  picture?: string;
  roles: UserRole[];
  permissions: UserPermissions;
  orgDisplayName: string;
}

interface AuthStore {
  userInfo: UserInfo;
}

// Create the context
const AuthContext = createContext<AuthStore | undefined>(undefined);

/**
 * AuthProvider - Provides auth state from the root layout loader.
 *
 * The root layout loader reads the user info from the AuthenticationService,
 * and on AuthenticationError logs out and redirects to /login. This provider
 * reads the loader data and exposes the user info to the app.
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const loaderData =
    useRouteLoaderData<RootLayoutLoaderData>(ROOT_LAYOUT_ROUTE_ID);

  if (!loaderData?.user) {
    throw new Error('User info not found');
  }

  const value: AuthStore = {
    userInfo: loaderData.user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use the auth store
export const useAuth = (): AuthStore => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

// Convenience hooks for common use cases
export const useUser = (): UserInfo => {
  const { userInfo } = useAuth();

  return userInfo;
};

/**
 * Effective organization resolved by middleware via auth0.effectiveOrganization.
 * Prefer this in React components over calling AuthenticationService directly.
 */
export const useEffectiveOrganization = (): OrganizationFieldsFragment => {
  return AuthenticationService.getInstance().getEffectiveOrganization();
};

export const usePermissions = (): UserPermissions => {
  const userInfo = useUser();

  return userInfo.permissions;
};

export const useUserRoles = (): UserRole[] => {
  const userInfo = useUser();

  return userInfo.roles;
};

// Convenience hooks for role checking
export const useIsLdtCustomerSuccess = (): boolean => {
  return isCustomerSuccess();
};

export const useIsOrderingPhysician = (): boolean => {
  return isOrderingPhysician();
};

export const useIsLdtSalesUser = (): boolean => {
  return isSalesUser();
};

export const useIsLdtSalesOrCustomerSuccessUser = (): boolean => {
  return isCustomerSuccessOrSalesUser();
};

export const useHasRole = (targetRoleName: UserRole): boolean => {
  const userRoles = useUserRoles();
  return hasRole(userRoles, targetRoleName);
};

export const useIsLabTech = (): boolean => {
  return isLabTech();
};

export const useIsClinicAdmin = (): boolean => {
  return isClinicAdmin();
};

export const useCanManageMembers = (): boolean => {
  return canManageMembers();
};
